import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Menu, NavService } from 'src/app/services/nav.service';
import { MeyMessageInboxClient, MessageInboxItem } from 'src/app/web-api-client';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ReadMailComponent } from '../../mails/read-mail/read-mail.component';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
})
export class SearchComponent implements OnInit, OnDestroy {
  public menuItems: Menu[] = [];
  public items: Menu[] = [];

  public searchResult: boolean = false;
  public searchResultEmpty: boolean = false;
  public text: string = '';
  open = false;

  // Message search properties
  public messageResults: MessageInboxItem[] = [];
  public isSearchingMessages: boolean = false;
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;
  private readonly MIN_SEARCH_LENGTH = 2;

  constructor(
    public navServices: NavService,
    private router: Router,
    private meyMessageInboxClient: MeyMessageInboxClient,
    private modalService: NgbModal,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.navServices.items.subscribe((menuItems) => (this.items = menuItems));
  }

  ngOnInit() {
    // Set up debounced message search
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      switchMap(term => {
        this.isSearchingMessages = true;
        return this.meyMessageInboxClient.search(term, 5).pipe(
          catchError(err => {
            console.error('Message search failed:', err);
            return of({ items: [] });
          })
        );
      })
    ).subscribe(result => {
      this.messageResults = result.items || [];
      this.isSearchingMessages = false;
      this.changeDetectorRef.detectChanges();
    });
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.removeFix();
  }

  // Check if user is on mail route
  isOnMailRoute(): boolean {
    return this.router.url.startsWith('/mail');
  }

  // menu open
  openMenu() {
    this.open = !this.open;
  }

  searchToggle() {
    this.navServices.search = false;
    document.getElementsByTagName('body')[0].classList.remove('offcanvas');
  }

  searchTerm(term: string) {
    term ? this.addFix() : this.removeFix();
    if (!term) return (this.menuItems = []);
    let items: Menu[] = [];
    term = term.toLowerCase();
    this.items.filter((menuItems) => {
      if (!menuItems?.title) return false;
      if (
        menuItems.title.toLowerCase().includes(term) &&
        menuItems.type === 'link'
      ) {
        items.push(menuItems);
      }
      if (!menuItems.children) return false;
      menuItems.children.filter((subItems) => {
        if (
          subItems.title?.toLowerCase().includes(term) &&
          subItems.type === 'link'
        ) {
          subItems.icon = menuItems.icon;
          items.push(subItems);
        }
        if (!subItems.children) return false;
        subItems.children.filter((suSubItems) => {
          if (suSubItems.title?.toLowerCase().includes(term)) {
            suSubItems.icon = menuItems.icon;
            items.push(suSubItems);
          }
        });
        return true;
      });
      this.checkSearchResultEmpty(items);
      this.menuItems = items;
      return true;
    });

    // Trigger message search if on mail route and term is long enough
    if (term && term.length >= this.MIN_SEARCH_LENGTH && this.isOnMailRoute()) {
      this.isSearchingMessages = true;
      this.searchSubject.next(term);
    } else {
      this.messageResults = [];
      this.isSearchingMessages = false;
    }

    return true;
  }

  checkSearchResultEmpty(items: any) {
    if (!items.length) this.searchResultEmpty = true;
    else this.searchResultEmpty = false;
  }

  addFix() {
    this.searchResult = true;
    document.body.classList.add('offcanvas');
  }

  removeFix() {
    this.searchResult = false;
    this.text = '';
    this.messageResults = [];
    this.isSearchingMessages = false;
    document.body.classList.remove('offcanvas');
  }

  onMessageClick(message: MessageInboxItem): void {
    this.removeFix();
    // Open the message in a modal (same behavior as mail-inbox component)
    const modalRef = this.modalService.open(ReadMailComponent, {
      size: 'xl',
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.message = message;
  }
}
